import { Component, input, signal, computed, ElementRef, effect, model, ChangeDetectionStrategy } from "@angular/core";
import { FormsModule } from "@angular/forms";

import { TypeManagerDecorator } from "../../main/type.map.service";
import { Workflow as WorkflowTypes } from "./service/Workflow";
import { WorkflowStageRulesComponent } from "./workflow.stage.rules.component";
import { S25Util } from "../../util/s25-util";
import { ExpandTrigger, WorkflowSteps } from "./workflow.process.types";
import { WorkflowUtil } from "./workflow.util";

@TypeManagerDecorator("workflow-stage-rules-step")
@Component({
    selector: "workflow-stage-rules-step",
    template: `
        <div class="stage-rules-step-container">
            <div class="step-header">
                <h3>Stage Rules</h3>
                <p class="step-description">
                    Configure rules for each stage to control when they are enabled or disabled. Only stages that
                    already have rules are listed below; use the dropdown to choose a stage and add a rule.
                </p>
            </div>

            @if (rulePresenceLoading()) {
                <p class="loading-hint">Loading stage rules…</p>
            }

            @if (!workflowProcess().workflowProcessId) {
                <div class="rules-unavailable">
                    <p>Stage rules will be available after saving the workflow process.</p>
                </div>
            } @else if (!(workflowProcess().stages || []).length) {
                <div class="no-stages">
                    <p>No stages defined. Please add stages in the previous step.</p>
                </div>
            } @else {
                <div class="add-rule-section">
                    <label for="add-stage-select">Select a stage to add a rule</label>
                    <select
                        id="add-stage-select"
                        class="stage-picker"
                        [ngModel]="addStageSelectValue()"
                        (ngModelChange)="onAddStageSelectChange($event)"
                    >
                        <option value="">Choose a stage…</option>
                        @for (stage of eligibleStagesForRules(); track stage.workflowStageId) {
                            <option [value]="stage.workflowStageId">{{ stage.stageName }}</option>
                        }
                    </select>
                    @if (selectedStageForAdd()) {
                        <div class="add-stage-editor">
                            <div class="add-stage-editor-toolbar">
                                <button
                                    type="button"
                                    class="aw-button aw-button--danger--outline"
                                    (click)="cancelAddStage()"
                                >
                                    Cancel
                                </button>
                            </div>
                            <workflow-stage-rules
                                [selectedStage]="selectedStageForAdd()"
                                [workflowProcessId]="workflowProcess().workflowProcessId"
                                (rulesChanged)="onStageRulesMutated()"
                            ></workflow-stage-rules>
                        </div>
                    }
                </div>

                <div class="stages-list">
                    @for (stage of stagesWithRules(); track stage.workflowStageId) {
                        <div class="stage-item" [attr.data-stage-id]="stage.workflowStageId || stage.id">
                            <div class="stage-header" (click)="toggleStageExpansion(stage)">
                                <div class="stage-info">
                                    <span class="stage-name">{{ stage.stageName }}</span>
                                    <span class="stage-flags">
                                        @if (stage.isStartStage) {
                                            <span class="flag start">Start</span>
                                        }
                                        @if (stage.isTerminal) {
                                            <span class="flag terminal">Terminal</span>
                                        }
                                        @if (stage.isAccumulator) {
                                            <span class="flag accumulator">Accumulator</span>
                                        }
                                    </span>
                                </div>
                                <div class="stage-actions">
                                    <span class="expand-icon" [class.expanded]="isStageExpanded(stage)">
                                        {{ isStageExpanded(stage) ? "▼" : "▶" }}
                                    </span>
                                </div>
                            </div>

                            @if (isStageExpanded(stage)) {
                                <div class="stage-content">
                                    <workflow-stage-rules
                                        [selectedStage]="stage"
                                        [workflowProcessId]="workflowProcess().workflowProcessId"
                                        (rulesChanged)="onStageRulesMutated()"
                                    ></workflow-stage-rules>
                                </div>
                            }
                        </div>
                    }
                </div>

                @if (!rulePresenceLoading() && !stagesWithRules().length && !selectedStageForAdd()) {
                    <div class="no-stages">
                        <p>No stage rules configured yet. Choose a stage above to add a rule.</p>
                    </div>
                }
            }
        </div>
    `,
    styles: `
        .stage-rules-step-container {
            padding: 20px;
        }

        .step-header {
            margin-bottom: 24px;
        }

        .step-header h3 {
            margin: 0 0 8px 0;
            color: var(--color--text-strong);
        }

        .step-description {
            margin: 0;
            color: var(--color--text-muted);
            font-size: 14px;
        }

        .loading-hint {
            color: var(--color--text-muted);
            margin-bottom: 16px;
        }

        .add-rule-section {
            margin-bottom: 24px;
            padding: 16px;
            background: var(--color--surface-2-bg);
            border: 1px solid var(--color--border);
            border-radius: 8px;
        }

        .add-rule-section label {
            display: block;
            margin-bottom: 8px;
            font-weight: 500;
            color: var(--color--text-strong);
        }

        .add-stage-editor {
            margin-top: 16px;
        }

        .add-stage-editor-toolbar {
            display: flex;
            justify-content: flex-end;
            margin-bottom: 12px;
        }

        .stages-list {
            display: flex;
            flex-direction: column;
            gap: 16px;
        }

        .stage-item {
            background: var(--color--surface-bg);
            border: 1px solid var(--color--border);
            border-radius: 8px;
            overflow: hidden;
        }

        .stage-header {
            padding: 16px;
            background: var(--color--surface-2-bg);
            cursor: pointer;
            display: flex;
            justify-content: space-between;
            align-items: center;
            transition: background 0.2s;
        }

        .stage-header:hover {
            background: var(--color--surface-2-bg-hover);
        }

        .stage-info {
            display: flex;
            align-items: center;
            gap: 12px;
        }

        .stage-name {
            font-weight: 500;
            color: var(--color--text-strong);
        }

        .stage-flags {
            display: flex;
            gap: 8px;
        }

        .flag {
            padding: 2px 8px;
            border-radius: 12px;
            font-size: 11px;
            font-weight: 500;
            text-transform: uppercase;
        }

        .flag.start {
            background: var(--color--registration);
            color: white;
        }

        .flag.terminal {
            background: var(--color--danger);
            color: white;
        }

        .flag.accumulator {
            background: var(--color--link-text);
            color: white;
        }

        .expand-icon {
            color: var(--color--text-muted);
            font-size: 12px;
            transition: transform 0.2s;
        }

        .expand-icon.expanded {
            transform: rotate(90deg);
        }

        .stage-content {
            padding: 20px;
            border-top: 1px solid var(--color--border);
        }

        .rules-unavailable {
            text-align: center;
            padding: 40px 20px;
            color: var(--color--text-muted);
            background: var(--color--surface-2-bg);
            border-radius: 6px;
        }

        .stage-picker {
            padding: 8px 12px;
            border: 1px solid var(--color--border-bold);
            border-radius: 4px;
            background: var(--color--surface-bg);
            min-width: 240px;
        }

        .no-stages {
            text-align: center;
            padding: 40px 20px;
            color: var(--color--text-muted);
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [WorkflowStageRulesComponent, FormsModule],
})
export class WorkflowStageRulesStepComponent {
    workflowProcess = input.required<WorkflowTypes.WorkflowProcess>();
    selectedStageTrigger = model<ExpandTrigger>();

    private expandedStages = signal<Set<number | string>>(new Set());
    stagesWithRules = signal<WorkflowTypes.WorkflowStage[]>([]);
    rulePresenceLoading = signal(false);
    selectedStageForAdd = signal<WorkflowTypes.WorkflowStage | null>(null);
    /** Value of the add-rule stage dropdown; empty string is the placeholder option. */
    addStageSelectValue = signal<string>("");

    readonly stageIdsWithRules = computed(() => {
        const ids = new Set<number>();
        for (const s of this.stagesWithRules()) {
            if (S25Util.isDefined(s.workflowStageId)) ids.add(s.workflowStageId!);
        }
        return ids;
    });

    readonly eligibleStagesForRules = computed(() => {
        const wp = this.workflowProcess();
        if (!wp.workflowProcessId) return [];
        return (wp.stages || []).filter((s) => !s.isAccumulator && !s.isApAccumulator && s.workflowStageId);
    });

    constructor(private el: ElementRef) {
        effect(() => {
            const expandTrigger = this.selectedStageTrigger();
            if (expandTrigger && expandTrigger.step === WorkflowSteps.Stages && S25Util.isDefined(expandTrigger.id)) {
                this.expandStageById(expandTrigger.id);
                this.selectedStageTrigger.set(undefined);
            }
        });
        effect(() => {
            this.workflowProcess();
            void this.refreshStagesWithRules();
        });
        // Clear the add-rule stage dropdown when the selected stage is already a rule stage
        effect(() => {
            const id = this.selectedStageForAdd()?.workflowStageId;
            if (!S25Util.isDefined(id)) return;
            if (this.stageIdsWithRules().has(id!)) {
                this.selectedStageForAdd.set(null);
                this.addStageSelectValue.set("");
            }
        });
    }

    async refreshStagesWithRules() {
        this.rulePresenceLoading.set(true);
        try {
            const stages = await WorkflowUtil.loadStagesWithWorkflowRules(this.workflowProcess());
            this.stagesWithRules.set(stages);
        } finally {
            this.rulePresenceLoading.set(false);
        }
    }

    onStageRulesMutated() {
        void this.refreshStagesWithRules();
    }

    cancelAddStage() {
        this.selectedStageForAdd.set(null);
        this.addStageSelectValue.set("");
    }

    onAddStageSelectChange(raw: string) {
        this.addStageSelectValue.set(raw);
        if (!raw) {
            this.selectedStageForAdd.set(null);
            return;
        }
        const id = Number(raw);
        const stage = this.workflowProcess().stages?.find((s) => s.workflowStageId === id);
        if (!stage) {
            this.selectedStageForAdd.set(null);
            return;
        }
        if (this.stageIdsWithRules().has(id)) {
            this.selectedStageForAdd.set(null);
            this.expandStageById(id);
            return;
        }
        for (const s of this.stagesWithRules()) {
            if (this.isStageExpanded(s)) {
                this.toggleStageExpansion(s);
            }
        }
        this.selectedStageForAdd.set(stage);
    }

    toggleStageExpansion(stage: WorkflowTypes.WorkflowStage) {
        const stageKey = stage.workflowStageId || stage.id!;
        const expanded = this.expandedStages();

        if (expanded.has(stageKey)) {
            expanded.delete(stageKey);
        } else {
            expanded.add(stageKey);
        }

        this.expandedStages.set(new Set(expanded));
    }

    isStageExpanded(stage: WorkflowTypes.WorkflowStage): boolean {
        const stageKey = stage.workflowStageId || stage.id!;
        return this.expandedStages().has(stageKey);
    }

    expandStageById(stageId: number) {
        const stages = this.workflowProcess().stages;
        const stage = stages?.find((s) => s.workflowStageId === stageId || s.id === stageId);
        if (stage) {
            const stageKey = stage.workflowStageId || stage.id!;
            const expanded = this.expandedStages();
            if (!expanded.has(stageKey)) {
                expanded.add(stageKey);
                this.expandedStages.set(new Set(expanded));
            }
            // Scroll to the stage element
            setTimeout(() => {
                const stageEl = this.el.nativeElement.querySelector(`[data-stage-id="${stageId}"]`);
                stageEl?.scrollIntoView({ behavior: "smooth", block: "start" });
            });
        }
    }
}
